/**
 * TanStack Query hooks for Teacher portal.
 * All data scoped to the authenticated teacher's ClassTeacher assignments.
 * API: /api/teacher/* (server-side enforces ClassTeacher scope)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';
import type { ClassStatus, Weekday, ScheduleMode } from './use-classes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface TeacherClassSchedule {
  id: string;
  classId: string;
  mode: ScheduleMode;
  weekday?: Weekday | null;
  sessionDate?: string | null;
  startTime: string;
  endTime: string;
  roomId?: string | null;
  roomName?: string | null;
}

export interface TeacherClass {
  id: string;
  code: string;
  name: string;
  status: ClassStatus;
  startDate: string | null;
  endDate?: string | null;
  capacity: number;
  currentEnrollment: number;
  courseId: string;
  courseName?: string | null;
  courseCode?: string | null;
  courseLevel?: string | null;
  branchId: string;
  branchName?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  teacherRole: 'lead' | 'assistant';
  schedules: TeacherClassSchedule[];
}

export interface TeacherClassDetail extends TeacherClass {
  schedules: TeacherClassSchedule[];
}

export interface RosterEnrollment {
  id: string;
  student: {
    id: string;
    studentCode: string;
    fullName: string;
    dateOfBirth: string;
    gender?: string | null;
    phone?: string | null;
    email?: string | null;
    note?: string | null;
  };
  status: string;
  enrolledAt: string;
}

export interface RosterData {
  enrollments: RosterEnrollment[];
  schedules: TeacherClassSchedule[];
  currentEnrollment: number;
  capacity: number;
}

export interface AttendanceSession {
  id: string;
  classId: string;
  branchId: string;
  sessionDate: string;
  sessionNumber?: number | null;
  status: 'open' | 'closed';
  lessonId?: string | null;
  note?: string | null;
  takenBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AttendanceRecord {
  id: string;
  sessionId: string;
  enrollmentId: string;
  status: 'present' | 'absent' | 'late' | 'excused';
  checkInAt?: string | null;
  note?: string | null;
}

export interface AttendanceSessionWithRecords {
  session: AttendanceSession | null;
  records: (AttendanceRecord & { student: { studentCode: string; fullName: string } })[];
}

export interface GradeColumn {
  key: string;
  label: string;
  gradeType: string;
  maxScore: number;
  assignmentId?: string | null;
}

export interface GradeEntry {
  enrollmentId: string;
  columnKey: string;
  score?: number | null;
  feedback?: string | null;
  gradedAt?: string | null;
  gradeId?: string | null;
}

export interface GradesData {
  enrollments: { id: string; student: { studentCode: string; fullName: string } }[];
  columns: GradeColumn[];
  grades: GradeEntry[];
}

export interface Lesson {
  id: string;
  classId: string;
  orderNo: number;
  title: string;
  description?: string | null;
  scheduledDate?: string | null;
}

export interface Assignment {
  id: string;
  classId: string;
  lessonId?: string | null;
  title: string;
  description?: string | null;
  type: string;
  dueDate?: string | null;
  maxScore?: number | null;
  submittedCount?: number;
  totalCount?: number;
  lesson?: { title: string } | null;
}

export interface SubmissionFile {
  id: string;
  submissionId: string;
  fileName: string;
  fileSize?: number | null;
  contentType?: string | null;
  fileKey: string;
}

export interface Submission {
  id: string;
  assignmentId: string;
  enrollmentId: string;
  status: 'assigned' | 'submitted' | 'graded' | 'late';
  content?: string | null;
  submittedAt?: string | null;
  student: { studentCode: string; fullName: string };
  files: SubmissionFile[];
  grade?: { score: number | null; feedback: string | null } | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const teacherKeys = {
  all: ['teacher'] as const,
  classes: (params?: Record<string, string>) => ['teacher', 'classes', params] as const,
  classDetail: (id: string) => ['teacher', 'classes', id] as const,
  roster: (classId: string) => ['teacher', 'classes', classId, 'roster'] as const,
  attendance: (classId: string, date?: string) =>
    ['teacher', 'classes', classId, 'attendance', date] as const,
  attendanceSessions: (classId: string) =>
    ['teacher', 'classes', classId, 'attendance-sessions'] as const,
  grades: (classId: string) => ['teacher', 'classes', classId, 'grades'] as const,
  assignments: (classId: string) => ['teacher', 'classes', classId, 'assignments'] as const,
  lessons: (classId: string) => ['teacher', 'classes', classId, 'lessons'] as const,
  submissions: (assignmentId: string) => ['teacher', 'submissions', assignmentId] as const,
};

// ---------------------------------------------------------------------------
// Hooks — Teacher Classes
// ---------------------------------------------------------------------------
export function useTeacherClasses(params?: { status?: string; courseId?: string }) {
  return useQuery({
    queryKey: teacherKeys.classes(params as Record<string, string>),
    queryFn: () =>
      api.get<TeacherClass[]>('/api/teacher/classes', params as Record<string, string>),
  });
}

export function useTeacherClassDetail(classId: string) {
  return useQuery({
    queryKey: teacherKeys.classDetail(classId),
    queryFn: () => api.get<TeacherClassDetail>(`/api/teacher/classes/${classId}`),
    enabled: !!classId,
  });
}

export function useTeacherRoster(classId: string) {
  return useQuery({
    queryKey: teacherKeys.roster(classId),
    queryFn: () => api.get<RosterData>(`/api/teacher/classes/${classId}/roster`),
    enabled: !!classId,
  });
}

// ---------------------------------------------------------------------------
// Hooks — Attendance
// ---------------------------------------------------------------------------
export function useAttendanceByDate(classId: string, date: string) {
  return useQuery({
    queryKey: teacherKeys.attendance(classId, date),
    queryFn: () =>
      api.get<AttendanceSessionWithRecords>(
        `/api/teacher/classes/${classId}/attendance-by-date`,
        { date },
      ),
    enabled: !!classId && !!date,
  });
}

export function useAttendanceSessions(classId: string) {
  return useQuery({
    queryKey: teacherKeys.attendanceSessions(classId),
    queryFn: () =>
      api.get<{ items: AttendanceSession[] }>(
        `/api/teacher/classes/${classId}/attendance-sessions`,
      ),
    enabled: !!classId,
  });
}

export function useOpenAttendanceSession(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sessionDate: string; lessonId?: string; sessionNumber?: number; note?: string }) =>
      api.post<AttendanceSession>(`/api/classes/${classId}/attendance`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: teacherKeys.attendance(classId, vars.sessionDate) });
      void qc.invalidateQueries({ queryKey: teacherKeys.attendanceSessions(classId) });
    },
  });
}

export function useSaveAttendance(classId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      records: { enrollmentId: string; status: string; checkInAt?: string; note?: string }[];
      close?: boolean;
    }) => api.post<AttendanceRecord[]>(`/api/classes/${classId}/attendance/${sessionId}/records`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teacher', 'classes', classId, 'attendance'] });
      void qc.invalidateQueries({ queryKey: teacherKeys.attendanceSessions(classId) });
    },
  });
}

export function useCloseAttendanceSession(classId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.patch<AttendanceSession>(`/api/classes/${classId}/attendance/${sessionId}/close`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teacher', 'classes', classId, 'attendance'] });
      void qc.invalidateQueries({ queryKey: teacherKeys.attendanceSessions(classId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks — Grades
// ---------------------------------------------------------------------------
export function useClassGrades(classId: string) {
  return useQuery({
    queryKey: teacherKeys.grades(classId),
    // Use teacher-scoped endpoint which builds the composite matrix
    queryFn: () => api.get<GradesData>(`/api/teacher/classes/${classId}/grades`),
    enabled: !!classId,
  });
}

export function useUpsertGrade(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      gradeId?: string | null;
      enrollmentId: string;
      gradeType: string;
      assignmentId?: string;
      score: number;
      maxScore: number;
      feedback?: string;
    }) => {
      const { gradeId, ...rest } = body;
      if (gradeId) {
        // Existing grade — update via PATCH
        return api.patch<unknown>(`/api/grades/${gradeId}`, {
          score: rest.score,
          maxScore: rest.maxScore,
          feedback: rest.feedback,
        });
      }
      // New grade — create via POST (branchId omitted; server derives from enrollment)
      return api.post<unknown>('/api/grades', {
        enrollmentId: rest.enrollmentId,
        gradeType: rest.gradeType,
        assignmentId: rest.assignmentId,
        score: rest.score,
        maxScore: rest.maxScore,
        feedback: rest.feedback,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.grades(classId) });
    },
  });
}

export function useDeleteGrade(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (gradeId: string) => api.delete<unknown>(`/api/grades/${gradeId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.grades(classId) });
    },
  });
}

/**
 * "Grade columns" are virtual — derived server-side from distinct (gradeType, assignmentId)
 * combos in existing grades. There is no grade-column table in the DB.
 * This hook creates the first Grade entry for a given (enrollmentId, gradeType) combination
 * which causes the column to materialize on next fetch.
 * The grades UI uses the type + label concept locally; the server key is gradeType enum.
 *
 * NOTE: The "New Column" dialog in the UI creates a placeholder grade for the first enrollment,
 * or simply queues the column info for the teacher to fill in individually.
 * For MVP, we expose a helper that just refreshes the grades query.
 * The dialog has been removed from the UI in favour of inline cell entry.
 */
export function useCreateGradeColumn(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Columns auto-materialise when first grade for that gradeType is saved.
    // This mutation is a no-op kept for interface compatibility during migration.
    mutationFn: (_body: { gradeType: string; label: string; maxScore: number }) =>
      Promise.resolve(null as unknown as GradeColumn),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.grades(classId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks — Lessons
// ---------------------------------------------------------------------------
export function useClassLessons(classId: string) {
  return useQuery({
    queryKey: teacherKeys.lessons(classId),
    queryFn: () => api.get<Lesson[]>('/api/lessons', { classId }),
    enabled: !!classId,
  });
}

export function useCreateLesson(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { orderNo: number; title: string; description?: string; scheduledDate?: string }) =>
      api.post<Lesson>('/api/lessons', { ...body, classId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.lessons(classId) });
    },
  });
}

export function useUpdateLesson(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; title?: string; description?: string; scheduledDate?: string; orderNo?: number }) =>
      api.patch<Lesson>(`/api/lessons/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.lessons(classId) });
    },
  });
}

export function useDeleteLesson(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<unknown>(`/api/lessons/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.lessons(classId) });
      void qc.invalidateQueries({ queryKey: teacherKeys.assignments(classId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks — Assignments
// ---------------------------------------------------------------------------
export function useClassAssignments(classId: string) {
  return useQuery({
    queryKey: teacherKeys.assignments(classId),
    queryFn: () => api.get<Assignment[]>('/api/assignments', { classId }),
    enabled: !!classId,
  });
}

export function useCreateAssignment(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      title: string;
      description?: string;
      type: string;
      lessonId?: string;
      dueDate?: string;
      maxScore?: number;
    }) => api.post<Assignment>('/api/assignments', { ...body, classId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.assignments(classId) });
    },
  });
}

export function useUpdateAssignment(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; title?: string; description?: string; type?: string; lessonId?: string; dueDate?: string; maxScore?: number }) =>
      api.patch<Assignment>(`/api/assignments/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.assignments(classId) });
    },
  });
}

export function useDeleteAssignment(classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<unknown>(`/api/assignments/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.assignments(classId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Hooks — Submissions + Grading
// ---------------------------------------------------------------------------
export function useAssignmentSubmissions(assignmentId: string) {
  return useQuery({
    queryKey: teacherKeys.submissions(assignmentId),
    queryFn: () =>
      api.get<{ assignment: Assignment; items: Submission[] }>(
        `/api/submissions`,
        { assignmentId },
      ),
    enabled: !!assignmentId,
  });
}

export function useGradeSubmission(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      ...body
    }: { submissionId: string; score: number; feedback?: string }) =>
      api.put<Submission>(`/api/submissions/${submissionId}/grade`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teacherKeys.submissions(assignmentId) });
    },
  });
}

export async function getSubmissionFileDownloadUrl(
  submissionId: string,
  fileId: string,
): Promise<string> {
  const result = await api.get<{ url: string; expiresIn: number }>(
    `/api/submissions/${submissionId}/files/${fileId}/download-url`,
  );
  return result.url;
}
